import React, { useEffect, useRef, useState } from "react";
import "./TableSection.scss";
import CustomButton from "../../Common/CustomButton/CustomButton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { useConfigStore } from "../../../stores/config";
import { useAuthStore } from "../../../stores/authStore";
import { useParams } from "react-router-dom";

interface FileRecord {
    file_id: number;
    file_name: string;
    file_type: string;
    status: string;
    workspace: string;
    created_at: string;
    updated_at: string;
    formatedCreatedDate?: string;
}

type DeleteState = {
    visible: boolean;
    row: FileRecord;
};

const fileTypeBodyTemplate = (rowData: FileRecord) => {
    return (
        <Tag
            value={rowData.file_type}
            severity="info"
            style={{ backgroundColor: "var(--color-blue-100)", color: "var(--color-blue-500)" }}
        />
    );
};

const statusBodyTemplate = (rowData: FileRecord) => {
    const val = rowData.status;
    if (val?.toLowerCase() === "uploading") {
        return (
            <span className="flex items-center gap-2">
                <i className="pi pi-spinner pi-spin" /> Uploading
            </span>
        );
    }
    if (val?.toLowerCase() === "error" || val?.toLowerCase() === "failed" ) {
        return <Tag value="Error" severity="danger" />;
    }
    if (val?.toLowerCase() === "deleted") {
        return <Tag value="Deleted" severity="info" />;
    }
    return (
        <Tag
            value={rowData.status}
            severity="success"
            style={{ backgroundColor: "var(--color-green-100)", color: "var(--status-success)" }}
        />
    );
};

type UploadStatus = "queued" | "uploading" | "success" | "error";

type PendingUpload = {
    id: string;
    file: File;
    fileName: string;
    file_type?: string;
    status: UploadStatus;
    error?: string;
};

const TableSection: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { reportsArray } = useConfigStore();
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [loading, setLoading] = useState(false);
    const fetchReports = useConfigStore((state) => state.fetchReports);
    const uploadFile = useConfigStore((state) => state.uploadFile);
    const deleteFile = useConfigStore((state) => state.deleteFile);
    const currentPage = Math.floor(first / rows) + 1;
    const [data, setData] = useState<FileRecord[]>(reportsArray?.data ?? []);
    const [optimisticUploads, setOptimisticUploads] = useState<FileRecord[]>([]);
    const [totalRecords, setTotalRecords] = useState(
        reportsArray?.data?.length
    );
    const [deleteVisible, setDeleteVisible] = useState<DeleteState>({
        visible: false,
        row: {} as FileRecord,
    });
    const { negotiation_id, setNegotiationId  } = useAuthStore();
    const { negotiation_id: negotiationIdParam } = useParams<{ negotiation_id?: string }>();

    useEffect(() => {
        if (negotiationIdParam && negotiationIdParam !== String(negotiation_id)) {
            setNegotiationId(negotiationIdParam);
        }
    }, [negotiationIdParam]);

    const actionsBodyTemplate = (rowData: any) => {
        return (
            <div className="flex gap-2">
                <CustomButton
                    label="Cancel"
                    icon="pi pi-times"
                    iconPosition="left"
                    onClick={() =>
                        setDeleteVisible({
                            visible: true,
                            row: rowData,
                        })
                    }
                    iconsOnly={true}
                    variant="tertiary"
                />
            </div>
        );
    };

    useEffect(() => {
        if (Array.isArray(reportsArray?.data)) {
            setData(reportsArray.data);
            setTotalRecords(reportsArray?.pagination?.total_files);
        }
    }, [reportsArray]);

    const handleHide = () => {
        if (!visible) return;
        setVisible(false);
    };


    const handleUpdate = () => {
        setVisible(true);
    };

    const footerContent = (
        <div className="flex justify-end gap-2">
            <CustomButton
                label="Cancel"
                variant="secondary"
                iconsOnly={false}
                onClick={handleHide}
            />
            <CustomButton label="Close" iconsOnly={false} onClick={handleHide} />
        </div>
    );

    const handleDelete = async () => {
        setDeleteVisible({ ...deleteVisible, visible: false });
        const payload = {
            file_id: deleteVisible.row.file_id,
        };
        await deleteFile(payload);
        const negId = negotiation_id ? parseInt(String(negotiation_id)) : undefined;
        await fetchReports(1, 10, negId);
    };

    const deleteFooterContent = (
        <div className="flex justify-end gap-2">
            <CustomButton
                label="Cancel"
                variant="secondary"
                iconsOnly={false}
                onClick={() =>
                    setDeleteVisible({ ...deleteVisible, visible: false })
                }
            />
            <CustomButton label="OK" iconsOnly={false} onClick={handleDelete} />
        </div>
    );

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            handleHide();
            const newItems: PendingUpload[] = Array.from(files).map((f) => ({
                id: `${f.name}-${f.size}-${f.lastModified}`,
                file: f,
                fileName: f.name,
                status: "queued",
            }));

            for (const item of newItems) {
                const tempId = -Math.abs(Number(`${Date.now()}${Math.floor(Math.random()*1000)}`));
                const optimistic: FileRecord = {
                    file_id: tempId,
                    file_name: item.fileName,
                    file_type: "",
                    status: "Uploading",
                    workspace: "",
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    formatedCreatedDate: new Date().toLocaleDateString(),
                };
                setOptimisticUploads((prev) => [...prev, optimistic]);

                (async () => {
                    try {
                        const payload = {
                            negotiation_id,
                            index_id: 1,
                            file: item.file,
                        };
                        const res: any = await uploadFile(payload);
                        const ok = res?.success !== false;
                        if (!ok) {
                            setOptimisticUploads((prev) => prev.map((r) => r.file_id === tempId ? { ...r, status: "Error" } : r));
                        } else {
                            setOptimisticUploads((prev) => prev.filter((r) => r.file_id !== tempId));
                        }
                    } catch (e) {
                        setOptimisticUploads((prev) => prev.map((r) => r.file_id === tempId ? { ...r, status: "Error" } : r));
                    }
                })();
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleFileUpload = () => {
        fileInputRef.current?.click();
    };

    const load = async (page = currentPage, pageSize = rows) => {
        setLoading(true);
        try {
            const negId = negotiation_id ? parseInt(String(negotiation_id)) : undefined;
            fetchReports(page, pageSize, negId);
        } finally {
            setLoading(false);
        }
    };

    const onPage = (e: any) => {
        const nextPage = (e.page ?? 0) + 1;
        const nextRows = e.rows;

        setFirst(e.first);
        setRows(nextRows);
        const negId = negotiation_id ? parseInt(String(negotiation_id)) : undefined;
        load(nextPage, nextRows);
        fetchReports(nextPage, nextRows, negId);
    };

    return (
        <div className="tablesection">
            <div className="flex justify-between tablesection-hero">
                <p className="tablesection-title my-0">
                    Total Files Uploaded ({totalRecords})
                </p>
                <CustomButton
                    label="Add Files"
                    icon="pi pi-plus"
                    iconPosition="left"
                    onClick={handleUpdate}
                    iconsOnly={false}
                    variant="outlined"
                />
            </div>

            <DataTable
                value={[...optimisticUploads, ...data]}
                dataKey="file_id"
                className="tablesection-table"
                paginator
                lazy
                loading={loading}
                rows={rows}
                first={first}
                totalRecords={totalRecords}
                onPage={onPage}
                rowsPerPageOptions={[10, 25, 50]}
                scrollable
                scrollHeight="350px"
            >
                <Column field="file_name" header="File Name" sortable />
                <Column
                    field="file_type"
                    header="File Type"
                    body={fileTypeBodyTemplate}
                    sortable
                />
                <Column
                    field="status"
                    header="Process Status"
                    body={statusBodyTemplate}
                    sortable
                />
                <Column
                    field="formatedCreatedDate"
                    header="Upload Date (DD/MM/YYYY)"
                    sortable
                />
                <Column
                    header="Actions"
                    body={actionsBodyTemplate}
                    style={{ minWidth: "8rem" }}
                />
            </DataTable>

            <Dialog
                header="Add Files"
                visible={visible}
                style={{ width: "50vw" }}
                footer={footerContent}
                onHide={handleHide}
                className="upload-dialog"
            >
                <div className="flex">
                    <div className="flex items-center gap-3 ">
                            {/** 
                        <div className="filter-content">

                            <p className="filter-label my-0">File Type</p>
                            <CustomDropdown
                                placeholder="Select File Type"
                                options={fileTypes}
                                onSelect={(e) => console.log(e.value)}
                                selectedOption={fileTypes[0]}
                            />
                        </div>

                            */}
                        <input
                            ref={fileInputRef}
                            id="file-upload"
                            type="file"
                            className="hidden"
                            multiple
                            onChange={handleFileChange}
                        />
                        <div className="filter-content">
                            <p className="filter-label my-0 invisible">File Type</p>
                            <CustomButton
                                label="Select Files"
                                iconsOnly={false}
                                icon="pi pi-plus "
                                variant="outlined"
                                onClick={handleFileUpload}
                            />
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Delete Confirmation"
                visible={deleteVisible.visible}
                style={{ width: "25vw" }}
                footer={deleteFooterContent}
                onHide={() => {
                    if (!deleteVisible.visible) return;
                    setDeleteVisible({
                        ...deleteVisible,
                        visible: false,
                    });
                }}
            >
                <p className="my-0 confirmation-text">
                    Are you sure you want to delete{" "}
                    <strong>{deleteVisible.row.file_name}</strong> ?
                </p>
            </Dialog>
        </div>
    );
};

export default TableSection;
